import { useState, useCallback } from 'react';
import { api } from '../App';
import { toLocalDateString } from '../utils/dateUtils';
import {
  ReportFilters,
  VotesReportFilters,
  BillTrackingReport,
  LegislatorTrackingReport,
  TimeTrackingReport,
  ActivitySummaryReport,
  HotBillsReport,
  ReportType,
  ExportFormat
} from '../types/reports';

export interface HotBillsFilters extends ReportFilters {
  includePastEvents?: boolean;
}

/** Append Votes Report query params (matches workspace-reports GET handler). */
export function appendVotesReportFilterParams(params: URLSearchParams, filters: VotesReportFilters = {}) {
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.legislatorParty) params.append('legislatorParty', filters.legislatorParty);
  if (filters.legislatorChamber) params.append('legislatorChamber', filters.legislatorChamber);
  if (filters.billPosition) params.append('billPosition', filters.billPosition);
  if (filters.legislatorPosition) params.append('legislatorPosition', filters.legislatorPosition);
  if (filters.priorityOnly) params.append('priorityOnly', 'true');
  if (filters.hotBillsOnly) params.append('hotBillsOnly', 'true');
  if (filters.voteType) params.append('voteType', filters.voteType);
  if (filters.voteContext) params.append('voteContext', filters.voteContext);
  if (filters.hasNotes) params.append('hasNotes', 'true');
  if (filters.noteAuthor) params.append('noteAuthor', filters.noteAuthor);
  if (filters.noteTags) params.append('noteTags', filters.noteTags);
  if (filters.noteSearch) params.append('noteSearch', filters.noteSearch);
}

export const useWorkspaceReports = (workspaceId?: string, organizationId?: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBillTrackingReport = useCallback(async (filters: ReportFilters = {}): Promise<BillTrackingReport> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await api.get(`/workspace-reports/${workspaceId}/bill-tracking?${params.toString()}`);
      return response.data.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch bill tracking report';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId]);

  const fetchLegislatorTrackingReport = useCallback(async (filters: ReportFilters = {}): Promise<LegislatorTrackingReport> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await api.get(`/workspace-reports/${workspaceId}/legislator-tracking?${params.toString()}`);
      return response.data.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch legislator tracking report';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId]);

  const fetchTimeTrackingReport = useCallback(async (filters: ReportFilters = {}): Promise<TimeTrackingReport> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await api.get(`/workspace-reports/${workspaceId}/time-tracking?${params.toString()}`);
      return response.data.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch time tracking report';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId]);

  const fetchActivitySummaryReport = useCallback(async (filters: ReportFilters = {}): Promise<ActivitySummaryReport> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await api.get(`/workspace-reports/${workspaceId}/activity-summary?${params.toString()}`);
      return response.data.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch activity summary report';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId]);

  const fetchHotBillsReport = useCallback(async (filters: HotBillsFilters = {}): Promise<HotBillsReport> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.includePastEvents) params.append('includePastEvents', 'true');

      const url = organizationId
        ? `/org-reports/${organizationId}/hot-bills?${params.toString()}`
        : `/workspace-reports/${workspaceId}/hot-bills?${params.toString()}`;
      const response = await api.get(url);
      return response.data.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch hot bills report';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, organizationId]);

  const exportReport = useCallback(async (
    reportType: ReportType,
    format: ExportFormat = 'excel',
    filters: ReportFilters = {}
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const exportUrl = organizationId
        ? `/org-reports/${organizationId}/standard-export`
        : `/workspace-reports/${workspaceId}/export`;
      const response = await api.post(
        exportUrl,
        {
          reportType,
          format,
          ...filters
        },
        {
          responseType: 'blob' // Important for file downloads
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or create default
      const disposition = response.headers['content-disposition'];
      
      // Map format to correct file extension
      const extensionMap: Record<string, string> = {
        csv: 'csv',
        excel: 'xlsx',
        pdf: 'pdf',
        word: 'docx'
      };
      const extension = extensionMap[format] || 'xlsx';
      let filename = `${reportType}-report-${toLocalDateString(new Date())}.${extension}`;
      
      if (disposition && disposition.includes('filename=')) {
        const filenameMatch = disposition.match(/filename=([^;]+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err: any) {
      // When responseType is 'blob', error response data is also a Blob
      // We need to read it to get the actual error message
      let errorMessage = 'Failed to export report';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          errorMessage = parsed.error || parsed.message || errorMessage;
        } catch {
          // If parsing fails, use default message
        }
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      console.error('Export failed:', errorMessage, err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, organizationId]);

  const fetchLegislatorVotesOnTrackedBillsReport = useCallback(async (filters: VotesReportFilters = {}) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      appendVotesReportFilterParams(params, filters);
      
      const url = organizationId
        ? `/org-reports/${organizationId}/legislator-votes-on-tracked-bills?${params.toString()}`
        : `/workspace-reports/${workspaceId}/legislator-votes-on-tracked-bills?${params.toString()}`;
      const response = await api.get(url, { timeout: 60000 });
      return response.data.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch legislator votes on tracked bills report';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, organizationId]);

  return {
    isLoading,
    error,
    fetchBillTrackingReport,
    fetchLegislatorTrackingReport,
    fetchTimeTrackingReport,
    fetchActivitySummaryReport,
    fetchHotBillsReport,
    fetchLegislatorVotesOnTrackedBillsReport,
    exportReport
  };
};
